import "./style.scss";

import { View } from "@amzn/stencil-react-components/layout";
import { PageHeaderDropdown, PageHeaderDropdownButton } from "@amzn/stencil-react-components/page";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services/user-permissions";
import React, { FunctionComponent } from "react";
import { useLocation } from "react-router-dom";

import { InternalLink } from "../InternalLink/InternalLink";

export interface UserProfileProps {
  /**
   * Username
   */
  username: string | null;
}
const UserProfile: FunctionComponent<UserProfileProps> = ({ username }) => {
  const location = useLocation();
  const isAiForMe: boolean = location.pathname.startsWith(Paths.aiforme.url);
  const toggleMyAiTools = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_MY_AI_TOOLS);
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);

  const options = [
    <InternalLink
      key="1"
      to="/orders"
      testId="header-orders-link"
      onClick={() => {
        trackUxmCustomEvent("header-your-orders-link", {});
      }}
    >
      <PageHeaderDropdownButton
        data-cy="orders-link"
        data-testid="header-dropdown-option"
        className="header-primary-text orders-link header-dropdown-item"
      >
        {getString("header.orders")}
      </PageHeaderDropdownButton>
    </InternalLink>,
    <InternalLink
      key="2"
      to="https://appsense.ee.amazon.dev/orgSummary"
      target="_blank"
      onClick={() => {
        trackUxmCustomEvent("header-org-software-expense", {});
      }}
    >
      <PageHeaderDropdownButton
        className="header-primary-text orders-link header-dropdown-item"
        data-testid="header-dropdown-option"
      >
        {getString("header.orgSoftwareExpense")}
      </PageHeaderDropdownButton>
    </InternalLink>,
    ...(toggleLists
      ? [
          <InternalLink
            key="3"
            to="/lists"
            onClick={() => {
              trackUxmCustomEvent("header-lists", {});
            }}
          >
            <PageHeaderDropdownButton
              className="header-primary-text orders-link header-dropdown-item"
              data-testid="header-dropdown-option"
            >
              {getString("header.lists")}
            </PageHeaderDropdownButton>
          </InternalLink>,
        ]
      : []),
  ];

  const aiForMeOptions = [
    <InternalLink
      key="tools"
      to="/aiforme/mytools"
      testId="my-tools-link"
      onClick={() => {
        trackUxmCustomEvent("header-aiforme-my-tools-link", {});
      }}
    >
      <PageHeaderDropdownButton
        data-cy="my-tools-link"
        data-testid="header-dropdown-option"
        className="header-primary-text orders-link header-dropdown-item"
      >
        {getString("aiForMe.myContributions")}
      </PageHeaderDropdownButton>
    </InternalLink>,
    ...(toggleLists
      ? [
          <InternalLink
            key="2"
            to={Paths.aiForMeLists.url}
            onClick={() => {
              trackUxmCustomEvent("header-lists", {});
            }}
          >
            <PageHeaderDropdownButton
              className="header-primary-text orders-link header-dropdown-item"
              data-testid="header-dropdown-option"
            >
              {getString("header.lists")}
            </PageHeaderDropdownButton>
          </InternalLink>,
        ]
      : []),
  ];

  if (isAiForMe && !toggleMyAiTools) {
    return <UserProfilePic username={username} />;
  }

  return (
    <View alignItems="center" height="100%" display="flex" justifyContent="center">
      <PageHeaderDropdown
        key="user-profile"
        aria-label={getString("userProfile.pictureAriaLabel", { username: username })}
        className="header-dropdown"
        data-testid="header-dropdown"
        items={isAiForMe ? aiForMeOptions : options}
      >
        <UserProfilePic username={username} />
        <span data-testid="user-pic-text" className="header-primary-text my-orders">
          {getString(isAiForMe ? "aiForMe.myTools" : "header.myOrders")}
        </span>
      </PageHeaderDropdown>
    </View>
  );
};

export default UserProfile;

export const UserProfilePic: FunctionComponent<UserProfileProps> = ({ username }) => {
  return (
    <div
      data-testid="username-picture"
      className="image-circle"
      style={{
        backgroundImage: `url('https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${username}')`,
      }}
      title={username ?? undefined}
    />
  );
};
